import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../../../shadcn/components/ui/button";
import { TableCell, TableRow } from "../../../shadcn/components/ui/table";
import ListTable from "../../../Components/Table/ListTable";
import {GET_TEAM_DETAILS} from "../../Teams/TeamsService";
import {GET_PROFILE_BY_ID} from "../../Membership/MembershipService";
import {GET_RESOLVE_USERS} from "../../../graphql/userGraphql";
import {RESOLVE_CLUBS} from "../SuperAdminService";
import {useTranslation} from "../../../context/Internationalization";
import useGraphql from "../../../Hooks/useGraphql";
import useLog from "../../../Hooks/useLog";
import SectionHeader from "../../../shadcn/components/pro-blocks/application/section-headers/section-header";
import { toast } from "sonner";

const profileInvitationSchema = (t) => [
  {
    id: "from",
    label: t("from").charAt(0).toUpperCase() + t("from").slice(1),
    sortable: false,
    minWidth: 180,
  },
  {
    id: "to",
    label: t("to").charAt(0).toUpperCase() + t("to").slice(1),
    sortable: false,
    minWidth: 180,
  },
  {
    id: "referenceTo",
    label: t("referenceTo"),
    sortable: false,
    minWidth: 200,
  },
  {
    id: "status",
    label: t("status"),
    sortable: false,
    minWidth: 140,
  },
  {
    id: "type",
    label: t("type"),
    sortable: false,
    minWidth: 180,
  },
];

const ProfilesTableRow = ({ data = {}}) => {
  const [open, setOpen] = React.useState(false);
  const {t} = useTranslation();
  const invitations = data.invitations || [];

  const invitationRowElement = (invitation, index) => (
    <InvitationsRow
      key={invitation._id || `${invitation.fromId}-${invitation.toId}-${index}`}
      invitation={invitation}
    />
  );

  return (
      <>
        <TableRow>
          <TableCell className="px-4">
            <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="expand row"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
            >
              {open ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
            </Button>
          </TableCell>
          <TableCell role="rowheader" className="px-4 font-medium">{data?.firstName}</TableCell>
          <TableCell className="px-4">{data?.lastName}</TableCell>
          <TableCell className="px-4">{data?.team}</TableCell>
          <TableCell className="px-4">{data?.club}</TableCell>
        </TableRow>
        {open && (
          <TableRow>
            <TableCell className="px-4" colSpan={5}>
              <div className="m-2">
                <SectionHeader as="h3" size="base" className="mb-2" title={t("user_invitations")} />
                <ListTable
                  rows={invitations}
                  columns={profileInvitationSchema(t)}
                  rowElement={invitationRowElement}
                  tableAriaLabel={t("user_invitations")}
                  minWidth={880}
                  emptyMessage={t("no_notifications_found")}
                />
              </div>
            </TableCell>
          </TableRow>
        )}
      </>
  );
};


export const getDetailsFromInvitationType = (invitationType, id, setData, axiosGraphQL, errorLog) => {
  switch(invitationType) {
    case "userForProfile":
    case "coachForTeam":
    case "parentForProfile":
      axiosGraphQL({
        query: GET_RESOLVE_USERS,
        variables: {ids: [id]},
      }).then(response => {
        const parent = response?.data?.data?.resolveUserIds[0]
        setData(`${parent?.firstName} ${parent?.lastName}`)
      }).catch((error) => {
        errorLog(error, "ProfilesTableRow", "getDetailsFromInvitationType", "GET_RESOLVE_USERS");
        toast.error(error.message);
      });
      break;
    case "teamToClub":
      axiosGraphQL({
        query: GET_TEAM_DETAILS,
        variables: { teamIds: [id]},
      }).then((response) => {
        const teamDetails = response.data.data.teamDetails;
        setData(teamDetails?.name)
      }).catch((error) => {
        errorLog(error, "ProfilesTableRow", "getDetailsFromInvitationType", "GET_TEAM_DETAILS");
        toast.error(error.message);
      });
      break;
  }
}

export const getDetailsToInvitationType = (type, id, setData, axiosGraphQL, errorLog) => {
  switch(type) {
    case "team":
      axiosGraphQL({
        query: GET_TEAM_DETAILS,
        variables: { teamIds: [id]},
      }).then((response) => {
        const teamDetails = response.data.data.teamDetails;
        setData(teamDetails?.name)
      }).catch((error) => {
        errorLog(error, "ProfilesTableRow", "getDetailsToInvitationType", "GET_TEAM_DETAILS");
        toast.error(error.message);
      });
      break;
    case "user":
      axiosGraphQL({
        query: GET_RESOLVE_USERS,
        variables: {ids: [id]},
      }).then(response => {
        const parent = response?.data?.data?.resolveUserIds[0]
        setData(`${parent?.firstName} ${parent?.lastName}`)
      }).catch((error) => {
        errorLog(error, "ProfilesTableRow", "getDetailsToInvitationType", "GET_RESOLVE_USERS");
        toast.error(error.message);
      });
      break;
    case "club":
      axiosGraphQL({
        query: RESOLVE_CLUBS,
        variables: {ids: [id]},
      }).then(response => {
        const club = response?.data?.data?.ResolveClubs[0]
        setData(club.name)
      }).catch((error) => {
        errorLog(error, "ProfilesTableRow", "getDetailsToInvitationType", "RESOLVE_CLUB");
        toast.error(error.message);
      });
  }
}

const InvitationsRow = ({invitation}) => {
  const [fromDetails, setFromDetails] = useState('')
  const [toDetails, setToDetails] = useState('')
  const [itemDetails, setItemDetails] = useState('')
  const {axiosGraphQL} = useGraphql();
  const {errorLog} = useLog();

  useEffect(() => {
    getDetailsFromInvitationType(invitation.invitationType, invitation.fromId, setFromDetails, axiosGraphQL, errorLog)
    getDetailsToInvitationType(invitation.invitationToType, invitation.toId, setToDetails, axiosGraphQL, errorLog)
  if(invitation.context.itemType === 'profile') {
    axiosGraphQL({query: GET_PROFILE_BY_ID, variables: {ids: [invitation?.context?.itemId]}})
        .then((response) => {
          const profile = response.data.data.profilesById[0];
          setItemDetails(`${profile.firstName} ${profile.lastName}`)
        }).catch((error) => {
          errorLog(error, "InvitationsRow", "useEffect", "GET_PROFILE_BY_ID");
          toast.error(error.message);
        });
  } else if(invitation.context.itemType === 'team') {
      axiosGraphQL({
        query: GET_TEAM_DETAILS,
        variables: { teamIds: [invitation?.context?.itemId]},
      }).then((response) => {
            const teamDetails = response.data.data.teamDetails;
            setItemDetails(teamDetails?.name)
      }).catch((error) => {
          errorLog(error, "InvitationsRow", "useEffect", "GET_TEAM_DETAILS");
          toast.error(error.message);
      });
    }
  }, [])

  return (
      <TableRow key={invitation?.fromId}>
        <TableCell role="rowheader" className="px-4 font-medium">{fromDetails}</TableCell>
        <TableCell className="px-4">{toDetails}</TableCell>
        <TableCell className="px-4">{itemDetails}</TableCell>
        <TableCell className="px-4">{invitation?.status}</TableCell>
        <TableCell className="px-4">{invitation?.invitationType}</TableCell>
      </TableRow>
  )
}
ProfilesTableRow.propTypes = {};

export default ProfilesTableRow;
